/*
 * PRN-capable DFU transport. Port of pc-nrf-dfu-js DfuTransportPrn.js.
 *
 * Implements the 5 DFU primitives (create/write/crc/execute/select + abort)
 * on top of two abstract wire operations: writeCommand (control opcode +
 * params) and writeData (raw bytes for a 0x08 'Write' command).
 *
 * Reads happen out-of-band via onData(); subclasses call onData() whenever
 * the SLIP decoder produces a full message.
 */

import { DfuError, ErrorCode } from "./errors.js";
import { crc32 } from "../util/crc32.js";
import {
  DfuAbstractTransport,
  type ObjectType,
  type SelectResult,
  type CrcResult,
} from "./abstract-transport.js";

export type ParsedPacket = [opcode: number, payload: Uint8Array];

const READ_TIMEOUT_MS = 5000;

export abstract class DfuTransportPrn extends DfuAbstractTransport {
  protected readonly prn: number;
  protected mtu = 0;

  private lastReceivedPacket: ParsedPacket | undefined;
  private waitingForPacket:
    | { resolve: (p: ParsedPacket) => void; reject: (e: unknown) => void; timer: ReturnType<typeof setTimeout> }
    | undefined;

  constructor(packetReceiveNotification = 16) {
    super();
    if (packetReceiveNotification > 0xffff) {
      throw new DfuError(ErrorCode.ERROR_CAN_NOT_USE_HIGHER_PRN);
    }
    this.prn = packetReceiveNotification;
  }

  protected abstract writeCommand(bytes: Uint8Array): Promise<void>;
  protected abstract writeData(bytes: Uint8Array): Promise<void>;
  protected abstract ready(): Promise<void>;

  /** Subclasses call this with each fully-decoded message from the bootloader. */
  protected onData(bytes: Uint8Array): void {
    let parsed: ParsedPacket;
    try {
      parsed = this.parse(bytes);
    } catch (err) {
      if (this.waitingForPacket) {
        const w = this.waitingForPacket;
        this.waitingForPacket = undefined;
        clearTimeout(w.timer);
        w.reject(err);
        return;
      }
      // No-one waiting; drop and hope a later read sees it.
      return;
    }

    if (this.waitingForPacket) {
      const w = this.waitingForPacket;
      this.waitingForPacket = undefined;
      clearTimeout(w.timer);
      w.resolve(parsed);
      return;
    }

    if (this.lastReceivedPacket) {
      // Two un-read messages — protocol violation upstream.
      this.lastReceivedPacket = undefined;
      return;
    }
    this.lastReceivedPacket = parsed;
  }

  /** Called by subclasses when the underlying transport has gone away. */
  protected onTransportClosed(): void {
    if (this.waitingForPacket) {
      const w = this.waitingForPacket;
      this.waitingForPacket = undefined;
      clearTimeout(w.timer);
      w.reject(new DfuError(ErrorCode.ERROR_DEVICE_DISCONNECTED));
    }
  }

  protected read(): Promise<ParsedPacket> {
    if (this.waitingForPacket) {
      return Promise.reject(new DfuError(ErrorCode.ERROR_READ_CONFLICT));
    }
    if (this.lastReceivedPacket) {
      const p = this.lastReceivedPacket;
      this.lastReceivedPacket = undefined;
      return Promise.resolve(p);
    }
    return new Promise<ParsedPacket>((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.waitingForPacket && this.waitingForPacket.resolve === resolve) {
          this.waitingForPacket = undefined;
        }
        reject(new DfuError(ErrorCode.ERROR_TIMEOUT_READING_SERIAL));
      }, READ_TIMEOUT_MS);
      this.waitingForPacket = { resolve, reject, timer };
    });
  }

  private parse(bytes: Uint8Array): ParsedPacket {
    if (bytes[0] !== 0x60) {
      throw new DfuError(ErrorCode.ERROR_RESPONSE_NOT_START_WITH_0x60);
    }
    const opcode = bytes[1];
    const resultCode = bytes[2];
    if (resultCode === ErrorCode.ERROR_MESSAGE_RSP) {
      return [opcode, bytes.subarray(3)];
    }
    const resultCodeRsp = (ErrorCode.ERROR_MESSAGE_RSP << 8) + resultCode;
    if (resultCode === (ErrorCode.ERROR_RSP_EXT_ERROR & 0xff)) {
      const extendedErrorCode = bytes[3];
      const resultCodeExt = (ErrorCode.ERROR_MESSAGE_EXT << 8) + extendedErrorCode;
      throw new DfuError(resultCodeExt);
    }
    throw new DfuError(resultCodeRsp, `(rsp byte 0x${resultCode.toString(16)})`);
  }

  protected assertPacket(
    expectedOpcode: number,
    expectedLength: number,
    response: ParsedPacket,
  ): Uint8Array {
    const [opcode, bytes] = response;
    if (opcode !== expectedOpcode) {
      throw new DfuError(
        ErrorCode.ERROR_UNEXPECTED_RESPONSE_OPCODE,
        `Expected opcode ${expectedOpcode}, got ${opcode}.`,
      );
    }
    if (bytes.length !== expectedLength) {
      throw new DfuError(
        ErrorCode.ERROR_UNEXPECTED_RESPONSE_BYTES,
        `Expected ${expectedLength} bytes for opcode ${expectedOpcode}, got ${bytes.length}.`,
      );
    }
    return bytes;
  }

  override async createObject(type: ObjectType, size: number): Promise<void> {
    await this.ready();
    await this.writeCommand(
      new Uint8Array([0x01, type, size & 0xff, (size >> 8) & 0xff, (size >> 16) & 0xff, (size >> 24) & 0xff]),
    );
    const resp = await this.read();
    this.assertPacket(0x01, 0, resp);
  }

  override async writeObject(
    bytes: Uint8Array,
    crcSoFar: number | undefined,
    offsetSoFar: number,
  ): Promise<unknown> {
    await this.ready();
    return this.writeObjectPiece(bytes, crcSoFar, offsetSoFar, 0);
  }

  private async writeObjectPiece(
    bytes: Uint8Array,
    crcSoFar: number | undefined,
    offsetSoFar: number,
    prnCount: number,
  ): Promise<[number, number]> {
    await this.ready();
    if (this.mtu === 0) {
      throw new DfuError(ErrorCode.ERROR_PORT_NOT_OPEN, "MTU not negotiated.");
    }

    const sendLength = Math.min(this.mtu, bytes.length);
    const bytesToSend = bytes.subarray(0, sendLength);

    const newOffsetSoFar = offsetSoFar + sendLength;
    const newCrcSoFar = crc32(bytesToSend, crcSoFar);
    let newPrnCount = prnCount + 1;

    await this.writeData(bytesToSend);

    if (this.prn > 0 && newPrnCount >= this.prn) {
      newPrnCount = 0;
      const { offset, crc } = await this.readCrc();
      if (newOffsetSoFar !== offset || newCrcSoFar !== crc) {
        throw new DfuError(
          ErrorCode.ERROR_CRC_MISMATCH,
          `PRN mismatch at ${offset}/${newOffsetSoFar}, expected 0x${newCrcSoFar.toString(16)} got 0x${crc.toString(16)}.`,
        );
      }
    }

    if (sendLength < bytes.length) {
      return this.writeObjectPiece(bytes.subarray(sendLength), newCrcSoFar, newOffsetSoFar, newPrnCount);
    }
    return [newOffsetSoFar, newCrcSoFar];
  }

  private async readCrc(): Promise<CrcResult> {
    await this.ready();
    const resp = await this.read();
    const bytes = this.assertPacket(0x03, 8, resp);
    const view = new DataView(bytes.buffer, bytes.byteOffset);
    return { offset: view.getUint32(0, true), crc: view.getUint32(4, true) >>> 0 };
  }

  override async crcObject(): Promise<CrcResult> {
    await this.ready();
    await this.writeCommand(new Uint8Array([0x03]));
    return this.readCrc();
  }

  override async executeObject(): Promise<void> {
    await this.ready();
    await this.writeCommand(new Uint8Array([0x04]));
    const resp = await this.read();
    this.assertPacket(0x04, 0, resp);
  }

  override async selectObject(type: ObjectType): Promise<SelectResult> {
    await this.ready();
    await this.writeCommand(new Uint8Array([0x06, type]));
    const resp = await this.read();
    const bytes = this.assertPacket(0x06, 12, resp);
    const view = new DataView(bytes.buffer, bytes.byteOffset);
    return {
      chunkSize: view.getUint32(0, true),
      offset: view.getUint32(4, true),
      crc: view.getUint32(8, true) >>> 0,
    };
  }

  override async abortObject(): Promise<void> {
    await this.ready();
    await this.writeCommand(new Uint8Array([0x0c]));
  }
}
